import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Banknote, Check, Loader2, X } from "lucide-react";
import { API } from "../../App";

const money = (v) =>
  v == null ? "—" : `€${Number(v).toLocaleString("en-IE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Flag = ({ ok, label }) => (
  <span className="inline-flex items-center gap-1 text-xs">
    {ok
      ? <Check className="w-3 h-3 text-emerald-400" />
      : <X className="w-3 h-3 text-amber-400" />}
    <span className={ok ? "text-emerald-300" : "text-amber-300"}>{label}</span>
  </span>
);

/**
 * The staff Payment panel. NOT the admin drawer.
 *
 * Shows only what a technician needs before handing the keys back:
 * deposit paid?, balance paid?, amount due, total. There is deliberately
 * no Generate Invoice, no Add Service / line item, no Merchant PDF, no
 * Resend email, no upsell and no adjust-services — those stay admin-only
 * exactly as sweep part 2 left them.
 *
 * Rendered only when the staff member holds `see_payment_amounts`.
 * The amounts come from the server already redacted-or-not; this
 * component never computes a balance (`total − deposit` ignores promo
 * discounts and would overcharge in cash).
 */
export const StaffPaymentPanel = ({ booking, canTakeCash, onPaid, paidOverride }) => {
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!booking) return null;

  // `paidOverride` is owned by the PARENT. It used to be local state,
  // but `onPaid` triggers a reload that unmounts this panel, so the
  // local copy was thrown away and the panel still showed "unpaid"
  // immediately after a successful payment.
  const depositPaid = paidOverride?.deposit_paid ?? !!booking.deposit_paid;
  const balancePaid = paidOverride?.balance_paid ?? !!booking.balance_paid;
  // ALWAYS the server's stored discount-aware figure. Staff cannot type
  // or edit an amount, and partial payments are not possible.
  const amountDue = balancePaid
    ? 0
    : (booking.remaining_balance_due ?? booking.balance_amount);

  const takeCash = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/auth/bookings/${booking.booking_id}/mark-cash-paid`,
        { scope: confirming });
      if (r.data.recorded === false) {
        toast.info("Already recorded — not counted twice");
      } else {
        toast.success(`${money(r.data.amount)} cash recorded`);
      }
      setConfirming(null);
      onPaid?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record the cash payment");
    } finally {
      setBusy(false);
    }
  };

  // The confirmation MUST state the amount the server will actually
  // record, or the till won't reconcile. Taking cash for the balance
  // when the deposit was never paid sweeps the deposit in too (the
  // customer owes both), so the figure is deposit + balance in that
  // case. Mirrors the `scope="balance"` branch of mark-cash-paid.
  const confirmAmount = confirming === "deposit"
    ? booking.deposit_amount
    : (depositPaid ? amountDue
        : Number(amountDue || 0) + Number(booking.deposit_amount || 0));

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2"
         data-testid="staff-payment-panel">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">Payment</p>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <Flag ok={depositPaid} label={depositPaid ? "Deposit paid" : "Deposit unpaid"} />
        <Flag ok={balancePaid} label={balancePaid ? "Balance paid" : "Balance unpaid"} />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span className="text-slate-400">
          Total <span className="font-mono text-white"
                      data-testid="staff-payment-total">{money(booking.total_price)}</span>
        </span>
        <span className="text-slate-400">
          Deposit <span className="font-mono text-white"
                        data-testid="staff-payment-deposit">{money(booking.deposit_amount)}</span>
        </span>
        <span className="text-slate-400">
          Due now <span className={`font-mono ${amountDue > 0 ? "text-saturn-orange" : "text-emerald-400"}`}
                        data-testid="staff-payment-due">{money(amountDue)}</span>
        </span>
      </div>

      {canTakeCash && (!depositPaid || !balancePaid) && (
        <div className="flex flex-wrap gap-2 pt-1">
          {!depositPaid && (
            <Button size="sm" onClick={() => setConfirming("deposit")}
                    className="h-7 bg-emerald-700 hover:bg-emerald-600 text-white"
                    data-testid="staff-take-cash-deposit-btn">
              <Banknote className="w-3 h-3 mr-1" /> Take cash — deposit
            </Button>
          )}
          {!balancePaid && (
            <Button size="sm" onClick={() => setConfirming("balance")}
                    className="h-7 bg-emerald-700 hover:bg-emerald-600 text-white"
                    data-testid="staff-take-cash-balance-btn">
              <Banknote className="w-3 h-3 mr-1" /> Take cash — balance
            </Button>
          )}
        </div>
      )}

      <AlertDialog open={!!confirming} onOpenChange={(v) => !v && setConfirming(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-white"
                            data-testid="staff-cash-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="staff-cash-confirm-amount">
              Confirm {money(confirmAmount)} received in cash
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This is the exact amount the system says is due — it cannot be
              changed, and part-payments are not possible. It will be recorded
              against your name for the till.
              {confirming === "balance" && !depositPaid
                && " This includes the unpaid deposit."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-600 text-slate-300"
                               data-testid="staff-cash-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); takeCash(); }}
                               disabled={busy}
                               className="bg-emerald-700 hover:bg-emerald-600"
                               data-testid="staff-cash-confirm-btn">
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Yes, cash received
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
